import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { Location } from '@jobos/shared/types/location';
import { useToast } from '@/hooks/use-toast';

interface LocationsResponse {
  locations: Location[];
}

interface LocationDetailResponse {
  location: Location;
}

interface CreateLocationInput {
  clientId: string;
  managerId?: string | null;
  name?: string;
  addressLine1?: string;
  addressLine2?: string;
  city: string;
  state?: string;
  zip?: string;
}

interface UpdateLocationInput {
  clientId?: string;
  managerId?: string | null;
  name?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string;
  state?: string | null;
  zip?: string | null;
}

// Query keys
export const locationKeys = {
  all: ['locations'] as const,
  lists: () => [...locationKeys.all, 'list'] as const,
  list: (clientId?: string) => [...locationKeys.lists(), { clientId }] as const,
  details: () => [...locationKeys.all, 'detail'] as const,
  detail: (id: string) => [...locationKeys.details(), id] as const,
};

// Fetch all locations with optional client filter
export function useLocations(clientId?: string) {
  return useQuery({
    queryKey: locationKeys.list(clientId),
    queryFn: async () => {
      const response = await apiClient.get<LocationsResponse>('/locations', {
        params: clientId ? { client_id: clientId } : undefined,
      });
      return response.locations;
    },
  });
}

// Fetch unique cities with optional client filter
export function useUniqueCities(clientId?: string) {
  return useQuery({
    queryKey: [...locationKeys.all, 'cities', { clientId }] as const,
    queryFn: async () => {
      const response = await apiClient.get<{ cities: string[] }>('/locations/cities/unique', {
        params: clientId ? { client_id: clientId } : undefined,
      });
      return response.cities;
    },
  });
}

// Fetch single location by ID
export function useLocation(id: string | undefined) {
  return useQuery({
    queryKey: locationKeys.detail(id!),
    queryFn: async () => {
      const response = await apiClient.get<LocationDetailResponse>(`/locations/${id}`);
      return response.location;
    },
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Address photo
//
// One photo per service address. New inspection reports on a job at this address
// seed their cover from it, which is the reason it exists.
// ---------------------------------------------------------------------------

interface LocationPhotoResponse {
  photoR2Key: string | null;
  photoThumbKey: string | null;
  photoUrl: string | null;
  photoThumbUrl: string | null;
}

/**
 * Multipart, so it bypasses apiClient (which forces application/json) and hits
 * the backend directly — the same reason the job-file and receipt uploads do.
 */
async function putAddressPhoto(locationId: string, file: File): Promise<LocationPhotoResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
  const response = await fetch(`${apiBaseUrl}/locations/${locationId}/photo`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || 'Upload failed');
  }

  return (await response.json()) as LocationPhotoResponse;
}

export function useUploadLocationPhoto(locationId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (file: File) => putAddressPhoto(locationId!, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(locationId!) });
      toast({ title: 'Photo saved' });
    },
    onError: (error: Error) => {
      toast({ title: 'Upload failed', description: error.message, variant: 'destructive' });
    },
  });
}

export function useDeleteLocationPhoto(locationId: string | undefined) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/locations/${locationId}/photo`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(locationId!) });
      toast({ title: 'Photo removed' });
    },
    onError: (error: Error) => {
      toast({ title: 'Could not remove photo', description: error.message, variant: 'destructive' });
    },
  });
}

/**
 * Adopt a job file (an inspection report cover) as the address photo.
 *
 * The backend refuses to overwrite an address that already has one, and says so
 * in `promoted` — so this is safe to fire on every cover change.
 */
export function usePromoteFileToLocationPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ locationId, fileId }: { locationId: string; fileId: string }) => {
      const response = await apiClient.post<LocationPhotoResponse & { promoted: boolean }>(
        `/locations/${locationId}/photo/from-job-file`,
        { fileId },
      );
      return response;
    },
    onSuccess: (result, { locationId }) => {
      if (result.promoted) {
        queryClient.invalidateQueries({ queryKey: locationKeys.detail(locationId) });
      }
    },
  });
}

// Create location mutation
export function useCreateLocation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateLocationInput) => {
      const response = await apiClient.post<LocationDetailResponse>('/locations', data);
      return response.location;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      toast({
        title: 'Success',
        description: 'Service address created successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create service address',
        variant: 'destructive',
      });
    },
  });
}

// Update location mutation
export function useUpdateLocation(id: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: UpdateLocationInput) => {
      const response = await apiClient.put<LocationDetailResponse>(`/locations/${id}`, data);
      return response.location;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      toast({
        title: 'Success',
        description: 'Service address updated successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update service address',
        variant: 'destructive',
      });
    },
  });
}

// Delete location mutation
export function useDeleteLocation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/locations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: locationKeys.lists() });
      toast({
        title: 'Success',
        description: 'Service address deleted successfully',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete service address',
        variant: 'destructive',
      });
    },
  });
}
